import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { dringendZuerst } from '@schnitt/shared';
import { api, type RoadmapComment, type RoadmapItem, type User } from '../api';
import { FeedbackSheet } from '../components/FeedbackSheet';
import { useTitel } from '../titel';
import { Sheet } from '../components/Sheet';
import { ContextMenu, type MenuItem } from '../components/ContextMenu';

/**
 * Die Roadmap: sehen, was kommt, mitreden, sortieren.
 *
 * `docs/ROADMAP.md` bleibt der geschriebene Plan; hier steht, was daraus wird.
 * Wer mitarbeitet, stimmt ab und schreibt Gedanken dazu; Admins sortieren und
 * entscheiden. Genau diese Reihenfolge lese ich beim nächsten Durchlauf.
 *
 * Kein „Fertig“-Knopf auf der Karte: abgehakt wird ein Punkt, wenn er gebaut
 * *und* geprüft ist -- das steht in der Datei und kommt beim nächsten Update
 * von selbst. Ein Knopf, der das vorwegnimmt, macht die Liste unwahr. Für den
 * Ausnahmefall steht es im Menü hinter den drei Punkten.
 */

const ZUSTAND: Record<RoadmapItem['status'], { label: string; klasse: string }> = {
  vorschlag: { label: 'Vorschlag', klasse: 'warn' },
  offen: { label: '', klasse: '' },
  laeuft: { label: 'wird gebaut', klasse: 'ok' },
  erledigt: { label: 'fertig', klasse: 'ok' },
  abgelehnt: { label: 'abgelehnt', klasse: 'err' },
  // Aus dem geschriebenen Plan verschwunden -- meist geteilt oder umbenannt.
  entfallen: { label: 'entfallen', klasse: '' },
};

export function Plan({ user, onBack }: { user: User; onBack: () => void }): ReactElement {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [feedback, setFeedback] = useState(false);
  const [bearbeiten, setBearbeiten] = useState<RoadmapItem | null>(null);
  const [gedanken, setGedanken] = useState<RoadmapItem | null>(null);
  const [zeigeFertige, setZeigeFertige] = useState(false);
  const [zeigeVorschlaege, setZeigeVorschlaege] = useState(false);
  const admin = user.role === 'admin';

  const laden = useCallback(async () => {
    try {
      setItems(dringendZuerst((await api.roadmap()).items));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die Roadmap ließ sich nicht laden.');
    }
  }, []);
  useEffect(() => void laden(), [laden]);

  const offen = items.filter((i) => i.status === 'offen' || i.status === 'laeuft');
  const vorschlaege = items.filter((i) => i.status === 'vorschlag');
  const fertig = items.filter((i) => i.status === 'erledigt' || i.status === 'entfallen');
  // Im Reiter steht dieselbe Zahl wie am Knopf: was auf eine Entscheidung
  // wartet.
  useTitel('Roadmap', vorschlaege.length ? `${vorschlaege.length} Vorschläge` : null);

  // Nach jeder Änderung neu sortiert: ein Haken bei „dringend“ hebt den
  // Punkt sofort nach oben (7.75), nicht erst nach dem Neuladen.
  const ersetzen = (item: RoadmapItem) => setItems((alt) => dringendZuerst(alt.map((i) => (i.id === item.id ? item : i))));

  const stimmen = async (item: RoadmapItem, richtung: 1 | -1) => {
    // Noch einmal dieselbe Stimme nimmt sie zurück.
    const wert = item.myVote === richtung ? 0 : richtung;
    ersetzen({
      ...item,
      myVote: wert,
      up: item.up - (item.myVote === 1 ? 1 : 0) + (wert === 1 ? 1 : 0),
      down: item.down - (item.myVote === -1 ? 1 : 0) + (wert === -1 ? 1 : 0),
    });
    try {
      ersetzen((await api.roadmapVote(item.id, wert)).item);
    } catch {
      await laden();
    }
  };

  const verschieben = async (id: string, ziel: number) => {
    const ohne = offen.filter((i) => i.id !== id);
    const vor = ohne[ziel - 1] ?? null;
    const nach = ohne[ziel] ?? null;
    try {
      await api.roadmapPatch(id, { beforeId: vor?.id ?? null, afterId: nach?.id ?? null });
    } finally {
      await laden();
    }
  };

  const setzen = async (id: string, patch: Record<string, unknown>) => {
    ersetzen((await api.roadmapPatch(id, patch)).item);
  };

  const loeschen = async (id: string) => {
    setItems((alt) => alt.filter((i) => i.id !== id));
    await api.roadmapRemove(id).catch(() => undefined);
    await laden();
  };

  return (
    <div className="page plan">
      <div className="topbar">
        <button className="ghost icon-btn" onClick={onBack} aria-label="Zurück">
          ‹
        </button>
        <h1>Roadmap</h1>
        <button className="primary small" onClick={() => setFeedback(true)}>
          Etwas melden
        </button>
      </div>

      {fehler ? <div className="card" style={{ borderColor: 'var(--danger)' }}>{fehler}</div> : null}

      <div className="plan-spalten">
        <div>
          <h3 className="section">
            <button className="klapp" onClick={() => setZeigeVorschlaege((v) => !v)} aria-expanded={zeigeVorschlaege}>
              {zeigeVorschlaege ? '▾' : '▸'} Vorschläge <span className="badge warn">{vorschlaege.length}</span>
            </button>
          </h3>
          {zeigeVorschlaege
            ? vorschlaege.map((item) => (
                <Karte
                  key={item.id}
                  item={item}
                  admin={admin}
                  onVote={stimmen}
                  onGedanken={() => setGedanken(item)}
                  onBearbeiten={() => setBearbeiten(item)}
                  onSetzen={setzen}
                  onLoeschen={loeschen}
                />
              ))
            : null}

          <h3 className="section">Als Nächstes</h3>
          <p className="hint">
            {admin ? 'Am Griff ziehen sortiert. Oben steht, was als Nächstes drankommt.' : 'Von oben nach unten: so wird gebaut.'}
            {' '}
            <span data-nummer-hinweis>Die Nummer davor ist der Name des Punkts, kein Platz — sie bleibt, wenn du sortierst.</span>
          </p>
          <Liste
            items={offen}
            admin={admin}
            onMove={verschieben}
            onVote={stimmen}
            onGedanken={setGedanken}
            onBearbeiten={setBearbeiten}
            onSetzen={setzen}
            onLoeschen={loeschen}
          />

          <h3 className="section">
            <button className="klapp" onClick={() => setZeigeFertige((v) => !v)} aria-expanded={zeigeFertige}>
              {zeigeFertige ? '▾' : '▸'} Fertig <span className="badge">{fertig.length}</span>
            </button>
          </h3>
          {zeigeFertige
            ? fertig.map((item) => (
                <Karte key={item.id} item={item} admin={admin} onVote={stimmen} onGedanken={() => setGedanken(item)} />
              ))
            : null}
        </div>
      </div>

      {feedback ? <FeedbackSheet bereich="Roadmap" onClose={() => setFeedback(false)} onDone={laden} /> : null}
      {bearbeiten ? (
        <BearbeitenBlatt
          item={bearbeiten}
          onClose={() => setBearbeiten(null)}
          onSaved={async () => {
            setBearbeiten(null);
            await laden();
          }}
        />
      ) : null}
      {gedanken ? <GedankenBlatt item={gedanken} onClose={() => setGedanken(null)} onDone={laden} /> : null}
    </div>
  );
}

/**
 * Der nächste Vorfahr, der wirklich rollt -- sonst das Dokument. Eine Seite
 * kann in einem Kasten stecken; wer dann das Fenster rollt, bewegt nichts.
 */
function rollKasten(): Element {
  let el: HTMLElement | null = document.querySelector('.plan-karte');
  while (el) {
    const cs = getComputedStyle(el);
    if (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
    el = el.parentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

function Liste({
  items,
  admin,
  onMove,
  onVote,
  onGedanken,
  onBearbeiten,
  onSetzen,
  onLoeschen,
}: {
  items: RoadmapItem[];
  admin: boolean;
  onMove: (id: string, ziel: number) => Promise<void>;
  onVote: (item: RoadmapItem, richtung: 1 | -1) => Promise<void>;
  onGedanken: (item: RoadmapItem) => void;
  onBearbeiten: (item: RoadmapItem) => void;
  onSetzen: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onLoeschen: (id: string) => Promise<void>;
}): ReactElement {
  const [zieht, setZieht] = useState<{ id: string; ziel: number } | null>(null);
  // Wo die Zeilen liegen: Mitte *und* linke Kante. Am grossen Schirm stehen
  // sie in Spalten (7.13e), und dann sagt die Höhe allein nicht, was vor was
  // kommt -- erst die Spalte, dann die Höhe.
  const reihen = useRef<{ id: string; links: number; mitte: number }[]>([]);
  const geste = useRef<{ id: string; ziel: number; hoehe: number; breite: number; dx: number; dy: number } | null>(null);
  const zeiger = useRef({ x: 0, y: 0 });
  const [schwebt, setSchwebt] = useState<{ x: number; y: number } | null>(null);

  const messen = (ids: Set<string>) => {
    reihen.current = [...document.querySelectorAll<HTMLElement>('[data-plan-id]')]
      .filter((el) => ids.has(el.dataset.planId ?? '') && !el.closest('.plan-schwebt'))
      .map((el) => {
        const box = el.getBoundingClientRect();
        return { id: el.dataset.planId ?? '', links: Math.round(box.left), mitte: box.top + box.height / 2 };
      });
  };

  /** In welcher Spalte ein Punkt liegt: die Kante, die ihm am nächsten ist. */
  const spalteVon = (x: number, kanten: number[]) => {
    let beste = 0;
    for (let i = 1; i < kanten.length; i++) if (Math.abs(kanten[i]! - x) < Math.abs(kanten[beste]! - x)) beste = i;
    return beste;
  };

  const start = (event: React.PointerEvent, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    messen(new Set(items.map((i) => i.id)));
    const el = (event.currentTarget as HTMLElement).closest<HTMLElement>('[data-plan-id]');
    const box = el?.getBoundingClientRect();
    const g = {
      id,
      ziel: items.findIndex((i) => i.id === id),
      hoehe: box?.height ?? 44,
      breite: box?.width ?? 320,
      dx: box ? event.clientX - box.left : 0,
      dy: box ? event.clientY - box.top : 0,
    };
    geste.current = g;
    zeiger.current = { x: event.clientX, y: event.clientY };
    setZieht({ id, ziel: g.ziel });
    setSchwebt({ x: event.clientX - g.dx, y: event.clientY - g.dy });
  };

  useEffect(() => {
    if (!zieht) return;
    const neuBerechnen = () => {
      const g = geste.current;
      if (!g) return;
      const ohne = reihen.current.filter((r) => r.id !== g.id);
      const kanten = [...new Set(ohne.map((r) => r.links))].sort((a, b) => a - b);
      const zs = spalteVon(zeiger.current.x, kanten);
      const davor = ohne.filter((r) => {
        const sp = spalteVon(r.links, kanten);
        return sp < zs || (sp === zs && r.mitte < zeiger.current.y);
      }).length;
      const ziel = Math.max(0, Math.min(items.length - 1, davor));
      if (ziel !== g.ziel) {
        geste.current = { ...g, ziel };
        setZieht({ id: g.id, ziel });
      }
    };
    let bild = 0;
    const bewegen = (event: PointerEvent) => {
      zeiger.current = { x: event.clientX, y: event.clientY };
      neuBerechnen();
      // Die schwebende Karte folgt dem Finger -- einmal je Bild, nicht je Ereignis.
      if (!bild) {
        bild = requestAnimationFrame(() => {
          bild = 0;
          const g = geste.current;
          if (g) setSchwebt({ x: zeiger.current.x - g.dx, y: zeiger.current.y - g.dy });
        });
      }
    };
    /**
     * Rollen, solange der Finger oben oder unten am Rand steht. Ohne das
     * endet jedes Ziehen an der Bildkante -- und in einer Liste mit dreißig
     * Punkten kommt man nie von unten nach oben.
     *
     * Gerollt wird der Kasten, der wirklich rollt: `window.scrollBy` bewegt
     * nichts, wenn der Inhalt in einem Kind mit eigenem Überlauf steckt.
     */
    const RAND = 90;
    const rollen = window.setInterval(() => {
      const y = zeiger.current.y;
      const h = window.innerHeight;
      const tempo = y < RAND ? -Math.max(6, (RAND - y) / 4) : y > h - RAND ? Math.max(6, (y - (h - RAND)) / 4) : 0;
      if (!tempo) return;
      rollKasten().scrollTop += tempo;
      // Nach dem Rollen liegen die Zeilen woanders: neu messen, sonst rechnet
      // die Einsortierung mit Zahlen von vorhin.
      messen(new Set(items.map((i) => i.id)));
      neuBerechnen();
    }, 16);
    const ende = () => {
      const g = geste.current;
      geste.current = null;
      if (bild) cancelAnimationFrame(bild);
      setZieht(null);
      setSchwebt(null);
      if (g && g.ziel !== items.findIndex((i) => i.id === g.id)) void onMove(g.id, g.ziel);
    };
    window.addEventListener('pointermove', bewegen);
    window.addEventListener('pointerup', ende);
    window.addEventListener('pointercancel', ende);
    return () => {
      window.clearInterval(rollen);
      if (bild) cancelAnimationFrame(bild);
      window.removeEventListener('pointermove', bewegen);
      window.removeEventListener('pointerup', ende);
      window.removeEventListener('pointercancel', ende);
    };
  }, [zieht, items, onMove]);

  // Während des Ziehens steht an der Zielstelle eine Lücke in der Höhe der
  // Karte, und die Karte selbst schwebt unter dem Finger (7.13e). Vorher
  // sprang die Zeile bei jedem Wechsel an ihren neuen Platz -- Sascha:
  // „besseres Drag & Drop“.
  const gezeigt = (() => {
    if (!zieht) return items;
    const rest = items.filter((i) => i.id !== zieht.id);
    const bewegt = items.find((i) => i.id === zieht.id);
    return bewegt ? [...rest.slice(0, zieht.ziel), bewegt, ...rest.slice(zieht.ziel)] : items;
  })();
  const bewegt = zieht ? items.find((i) => i.id === zieht.id) : undefined;
  const g = geste.current;

  return (
    <div className="plan-liste">
      {gezeigt.map((item, i) =>
        zieht && item.id === zieht.id ? (
          <div key={item.id} className="plan-luecke" style={{ height: g?.hoehe ?? 44 }} aria-hidden="true" />
        ) : (
          <Karte
            key={item.id}
            item={item}
            admin={admin}
            onVote={onVote}
            onGedanken={() => onGedanken(item)}
            onBearbeiten={() => onBearbeiten(item)}
            onSetzen={onSetzen}
            onLoeschen={onLoeschen}
            onGriff={admin ? (e) => start(e, item.id) : undefined}
          />
        ),
      )}
      {bewegt && schwebt && g ? (
        <div className="plan-schwebt" style={{ left: schwebt.x, top: schwebt.y, width: g.breite }} aria-hidden="true">
          <Karte item={bewegt} admin={admin} ziehend onVote={onVote} onGedanken={() => undefined} />
        </div>
      ) : null}
      {items.length === 0 ? <p className="hint">Nichts offen. Das kommt selten vor.</p> : null}
    </div>
  );
}

/** Der erste Satz -- so viel, wie eine Zeile trägt. */
function kurzVon(detail: string): string {
  const ganz = detail.replace(/\s+/g, ' ').trim();
  // Viele Punkte beginnen mit der Quelle in Klammern („(Sascha, 4. September: …)“);
  // die Zeile soll sagen, worum es geht, nicht wer es gesagt hat.
  const ohneKlammer = ganz.startsWith('(') && ganz.includes(')') ? ganz.slice(ganz.indexOf(')') + 1).replace(/^[\s.:—-]+/, '') : ganz;
  const quelle = ohneKlammer || ganz;
  // Satzende: Punkt, Ausrufe- oder Fragezeichen -- aber nicht nach einer Zahl („4. September“).
  const erster = quelle.split(/(?<=[^\d][.!?])\s/)[0] ?? '';
  return erster.length > 140 ? `${erster.slice(0, 137)}…` : erster;
}

function Karte({
  item,
  admin,
  ziehend,
  onVote,
  onGedanken,
  onBearbeiten,
  onSetzen,
  onLoeschen,
  onGriff,
}: {
  item: RoadmapItem;
  admin: boolean;
  ziehend?: boolean;
  onVote: (item: RoadmapItem, richtung: 1 | -1) => Promise<void>;
  onGedanken: () => void;
  onBearbeiten?: () => void;
  onSetzen?: (id: string, patch: Record<string, unknown>) => Promise<void>;
  onLoeschen?: (id: string) => Promise<void>;
  onGriff?: (event: React.PointerEvent) => void;
}): ReactElement {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [offen, setOffen] = useState(false);
  const zustand = ZUSTAND[item.status];

  const menuItems: MenuItem[] = [
    ...(onBearbeiten ? [{ icon: 'text' as const, label: 'Bearbeiten', onSelect: onBearbeiten }] : []),
    {
      icon: 'sliders',
      label: item.urgent ? 'Nicht mehr dringend' : 'Als dringend markieren',
      onSelect: () => void onSetzen?.(item.id, { urgent: !item.urgent }),
    },
    // „Wird gebaut“ und „fertig“ setze *ich*, wenn ich es gebaut und geprüft
    // habe -- deshalb stehen sie hier nicht als Knöpfe. Was Sascha entscheidet:
    // die Reihenfolge, die Dringlichkeit, und ob ein Vorschlag aufgenommen wird.
    ...(item.status === 'vorschlag'
      ? [
          { icon: 'plus' as const, label: 'Aufnehmen', onSelect: () => void onSetzen?.(item.id, { status: 'offen' }) },
          { icon: 'trash' as const, label: 'Ablehnen', onSelect: () => void onSetzen?.(item.id, { status: 'abgelehnt' }), danger: true },
        ]
      : [
          { icon: 'back' as const, label: 'Zurück in die Vorschläge', onSelect: () => void onSetzen?.(item.id, { status: 'vorschlag' }) },
          // Ein Punkt aus der Datei kann nicht wirklich weg -- beim nächsten
          // Start stünde er wieder da. „Abgelehnt“ dagegen bleibt: den Zustand
          // fasst der Abgleich nicht an. Nur was in der App entstanden ist
          // (ohne Kennzeichen), lässt sich löschen.
          item.key
            ? { icon: 'trash' as const, label: 'Ablehnen', onSelect: () => void onSetzen?.(item.id, { status: 'abgelehnt' }), danger: true }
            : { icon: 'trash' as const, label: 'Löschen', onSelect: () => void onLoeschen?.(item.id), danger: true },
        ]),
  ];

  return (
    <div className={`plan-karte ${ziehend ? 'zieht' : ''} ${item.urgent ? 'dringend' : ''}`} data-plan-id={item.id}>
      <div className="plan-kopf">
        {/* 7.76: *keine* Platznummer mehr. Sascha, 9. September: „Du hast ja
            hier Nummern wie 7.24. Haben wir in der Roadmap noch mal eigene
            Nummerierungen? Können wir das nicht mal matchen?“ Es waren zwei
            Zahlen mit zwei Bedeutungen -- der Platz in der Warteschlange (der
            sich mit jedem Ziehen ändert) und der Name des Punkts (der bleibt,
            und an dem Commit, Changelog und Fahrplan hängen). Den Platz sieht
            man an der Liste selbst; also bleibt eine Zahl, der Name. */}
        {item.key ? <span className="plan-key" title="Der Name dieses Punkts — kein Platz in der Liste">{item.key}</span> : null}
        {/* 7.13e: die Zeile sagt ohne Aufklappen, worum es geht -- Titel und
            der erste Satz --, wie es steht und wie viele dafür sind. */}
        <div className="plan-text">
          <div className="plan-zeile1">
            <button className="plan-titel" onClick={() => setOffen((v) => !v)} title="Aufklappen" aria-expanded={offen}>
              {item.title}
            </button>
            {item.urgent ? <span className="badge err">dringend</span> : null}
            {zustand.label ? <span className={`badge ${zustand.klasse}`}>{zustand.label}</span> : null}
          </div>
          <div className="plan-zeile2">
            {!offen && item.detail ? <span className="plan-kurz">{kurzVon(item.detail)}</span> : <span className="plan-kurz" />}
            <div className="plan-zahlen">
              <button className={`stimme ${item.myVote === 1 ? 'an' : ''}`} onClick={() => void onVote(item, 1)} aria-label={`Dafür (${item.up})`}>
                ▲ {item.up}
              </button>
              <button className={`stimme ${item.myVote === -1 ? 'an' : ''}`} onClick={() => void onVote(item, -1)} aria-label={`Dagegen (${item.down})`}>
                ▼ {item.down}
              </button>
              <button className="stimme" onClick={onGedanken} aria-label={`Gedanken (${item.comments})`}>
                ✎ {item.comments}
              </button>
            </div>
          </div>
        </div>
        {admin ? (
          <>
            <button
              className="ghost plan-mehr"
              aria-label={`Mehr zu ${item.title}`}
              onClick={(e) => {
                const b = e.currentTarget.getBoundingClientRect();
                setMenu({ x: b.right, y: b.bottom + 4 });
              }}
            >
              ⋯
            </button>
            <button className="ghost plan-griff" aria-label={`${item.title} verschieben`} onPointerDown={onGriff}>
              ≡
            </button>
          </>
        ) : null}
      </div>

      {offen && item.detail ? <div className="plan-detail">{item.detail}</div> : null}
      {offen && item.imageUrl ? <img src={item.imageUrl} alt="" className="plan-bild" /> : null}
      {offen && (item.area || item.author) ? (
        <div className="hint">{[item.area, item.author].filter(Boolean).join(' · ')}</div>
      ) : null}

      {menu ? <ContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} align="right" /> : null}
    </div>
  );
}

/** Titel und Text ändern -- was in der Datei steht, ist ein Vorschlag, kein Gesetz. */
function BearbeitenBlatt({
  item,
  onClose,
  onSaved,
}: {
  item: RoadmapItem;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): ReactElement {
  const [title, setTitle] = useState(item.title);
  const [detail, setDetail] = useState(item.detail);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title="Punkt bearbeiten" onClose={onClose}>
      <label className="field">
        <span>Titel</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="field">
        <span>Beschreibung</span>
        <textarea rows={6} value={detail} onChange={(e) => setDetail(e.target.value)} />
      </label>
      <p className="hint">
        Gilt in der App. Beim nächsten Durchlauf ziehe ich sie in <code>docs/ROADMAP.md</code> nach.
      </p>
      <button
        className="primary block"
        disabled={busy || title.trim().length < 3}
        onClick={async () => {
          setBusy(true);
          try {
            await api.roadmapPatch(item.id, { title: title.trim(), detail: detail.trim() });
            await onSaved();
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Wird gespeichert…' : 'Speichern'}
      </button>
    </Sheet>
  );
}

/** Gedanken zu einem Punkt: warum, wie, was dagegen spricht. */
function GedankenBlatt({
  item,
  onClose,
  onDone,
}: {
  item: RoadmapItem;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactElement {
  const [liste, setListe] = useState<RoadmapComment[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setListe((await api.roadmapComments(item.id)).comments);
  }, [item.id]);
  useEffect(() => void laden(), [laden]);

  return (
    <Sheet title={item.title} onClose={onClose}>
      {liste.length === 0 ? <p className="hint">Noch nichts dazu gesagt.</p> : null}
      {liste.map((c) => (
        <div key={c.id} className="card" style={{ marginBottom: 8, padding: '10px 12px' }}>
          <div className="hint" style={{ marginBottom: 2 }}>
            {c.author} · {new Date(c.createdAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{c.text}</div>
        </div>
      ))}
      <label className="field">
        <span>Dein Gedanke</span>
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Warum, wie, was dagegen spricht…" />
      </label>
      <button
        className="primary block"
        disabled={busy || text.trim().length === 0}
        onClick={async () => {
          setBusy(true);
          try {
            await api.roadmapComment(item.id, text.trim());
            setText('');
            await laden();
            await onDone();
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Wird geschickt…' : 'Abschicken'}
      </button>
    </Sheet>
  );
}
